import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GripVertical, Pencil, Plus, Trash2, X } from "lucide-react";
import api, { fileToBase64 } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PAGES = [
  { key: "univers", label: "Univers Maasaï (portail)" },
  { key: "palais", label: "Palais Maasaï (accueil)" },
  { key: "comptoir", label: "Le Comptoir Maasaï" },
  { key: "services", label: "Maasaï Services" },
];

const TYPES = [
  { value: "texte", label: "Texte (titre + paragraphe)" },
  { value: "image_texte", label: "Image + texte" },
  { value: "cta", label: "Appel à l'action (titre + bouton)" },
];

const emptySection = {
  page: "palais",
  type: "texte",
  title: "",
  text: "",
  image: "",
  button_label: "",
  button_url: "",
  order: 0,
  visible: true,
};

export default function SectionsManager() {
  const [page, setPage] = useState("palais");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState(null);

  const load = () =>
    api.get(`/admin/sections?page=${page}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editing, page, order: editing.id ? editing.order : items.length };
      if (editing.id) {
        await api.put(`/admin/sections/${editing.id}`, payload);
      } else {
        await api.post("/admin/sections", payload);
      }
      toast.success("Section enregistrée.");
      setEditing(null);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/sections/${id}`);
    toast.success("Section supprimée.");
    load();
  };

  const toggleVisible = async (section, visible) => {
    await api.put(`/admin/sections/${section.id}`, { ...section, visible });
    load();
  };

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = items.map((i) => i.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    const reordered = [...items];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setItems(reordered);
    setDragId(null);
    await Promise.all(
      reordered.map((s, idx) => api.put(`/admin/sections/${s.id}/order`, { order: idx }))
    );
    toast.success("Ordre enregistré.");
  };

  const set = (key) => (e) => setEditing((s) => ({ ...s, [key]: e.target.value }));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditing((s) => ({ ...s, image: "" }));
    const b64 = await fileToBase64(file);
    setEditing((s) => ({ ...s, image: b64 }));
  };

  return (
    <div data-testid="sections-manager">
      <h2 className="mb-2 text-lg font-semibold text-ivory">Constructeur de sections</h2>
      <p className="mb-8 max-w-2xl text-xs leading-relaxed text-ivory/40">
        Ajoutez, déplacez (glisser-déposer), modifiez ou masquez des sections sur chaque page —
        sans toucher au code. Les sections personnalisées s'affichent en bas de la page choisie.
      </p>

      <div className="mb-8 flex flex-wrap items-center gap-2" data-testid="sections-page-tabs">
        {PAGES.map((p) => (
          <button
            key={p.key}
            onClick={() => setPage(p.key)}
            data-testid={`sections-tab-${p.key}`}
            className={`rounded-md border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors ${
              page === p.key
                ? "border-copper bg-copper text-ivory"
                : "border-white/10 text-ivory/60 hover:border-copper/50 hover:text-ivory"
            }`}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={() => setEditing({ ...emptySection, page })}
          className="btn-primary ml-auto !h-10 !px-5 !text-[11px]"
          data-testid="section-add-button"
        >
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          Ajouter une section
        </button>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="section-edit-modal">
          <form onSubmit={save} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto rounded-lg border border-white/10 bg-coal p-8">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-ivory">
                {editing.id ? "Modifier la section" : "Nouvelle section"}
              </h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Fermer">
                <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
              </button>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Type de section</label>
              <Select value={editing.type} onValueChange={(v) => setEditing((s) => ({ ...s, type: v }))}>
                <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="section-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-coal text-ivory">
                  {TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <input required value={editing.title} onChange={set("title")} placeholder="Titre de la section" className="input-dark !h-11" data-testid="section-title-input" />
            <textarea rows={4} value={editing.text} onChange={set("text")} placeholder="Texte (optionnel)" className="input-dark !h-auto py-3" data-testid="section-text-input" />
            {editing.type === "image_texte" && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Image</label>
                <input type="file" accept="image/*" onChange={uploadImage} className="w-full text-xs text-ivory/60 file:mr-3 file:rounded-md file:border-0 file:bg-copper file:px-4 file:py-2 file:text-xs file:font-semibold file:uppercase file:tracking-wider file:text-ivory" data-testid="section-image-input" />
                {editing.image && (
                  <img src={editing.image} alt="Aperçu" className="mt-3 h-24 rounded-md object-cover" />
                )}
              </div>
            )}
            {editing.type === "cta" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <input value={editing.button_label} onChange={set("button_label")} placeholder="Texte du bouton" className="input-dark !h-11" data-testid="section-button-label-input" />
                <input value={editing.button_url} onChange={set("button_url")} placeholder="Lien (ex : /reservation)" className="input-dark !h-11" data-testid="section-button-url-input" />
              </div>
            )}
            <label className="flex items-center gap-3 text-sm text-ivory/70">
              Visible
              <Switch checked={editing.visible} onCheckedChange={(v) => setEditing((s) => ({ ...s, visible: v }))} data-testid="section-visible-switch" />
            </label>
            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="section-save-button">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-ivory/40" data-testid="sections-empty">
          Aucune section personnalisée sur cette page. Ajoutez votre première section.
        </p>
      ) : (
        <div className="space-y-2" data-testid="sections-list">
          {items.map((s) => (
            <div
              key={s.id}
              draggable
              onDragStart={() => setDragId(s.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(s.id)}
              className={`flex items-center gap-4 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5 ${dragId === s.id ? "opacity-40" : ""}`}
              data-testid={`section-row-${s.id}`}
            >
              <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-ivory/30" strokeWidth={1.5} />
              <div className="min-w-0 flex-1">
                <p className={`truncate text-sm ${s.visible ? "text-ivory" : "text-ivory/35 line-through"}`}>
                  {s.title}
                </p>
                <p className="text-xs text-ivory/40">
                  {TYPES.find((t) => t.value === s.type)?.label}
                </p>
              </div>
              <Switch checked={s.visible} onCheckedChange={(v) => toggleVisible(s, v)} data-testid={`section-visible-${s.id}`} />
              <button onClick={() => setEditing({ ...s })} aria-label="Modifier" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold" data-testid={`section-edit-${s.id}`}>
                <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
              <button onClick={() => remove(s.id)} aria-label="Supprimer" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`section-delete-${s.id}`}>
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
